import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import Modal from 'react-modal'
import { Formik, Form, Field } from 'formik'
import { AiFillCloseCircle } from 'react-icons/ai'
import { IconContext } from 'react-icons'
import { Alert, Input } from '@wellbeingxltd/fe-lib/components'
import { GET_STORE, SUPPLIERS_PERMISSIONS_TO_VIEW } from '@wellbeingxltd/fe-lib/constants'
import AnimatedLogo from '@wellbeingxltd/fe-lib/images/dist/logo/AnimatedLogo.gif'
import { AnalyticsActions } from 'store/models/analytics'
import ManagersActions from 'store/models/managers'
import SupplierActions from 'store/models/supplier'
import emailSchema from './inviteEmailSchema'
import { Table, Button } from 'components'
import Profile from './Profile'
import ChangePassword from './ChangePassword'
import { columnsData, notifications } from './config'
import './styles.scss'

const Settings = () => {
    const { attributes = {} } = JSON.parse(localStorage.getItem('cognitoUser'))
    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [modalPasswordIsOpen, setModalPasswordIsOpen] = useState(false)
    const [isManager, setIsManager] = useState(false)
    const [updatedPermissions, setUpdatedPermissions] = useState([])
    const { supplier } = useSelector(GET_STORE.SUPPLIER)
    const { getManagersError, loadingAddManager, loadingManagers, loadingUpdateManager, managers, loadingUpdateRole } = useSelector(
        GET_STORE.MANAGERS
    )
    const dispatch = useDispatch()

    useEffect(() => {
        document.title = 'Settings | Betterspace Supplier'

        if (!managers.length) {
            dispatch(ManagersActions.getManagers())
        }
        if (!supplier || !Object.keys(supplier).length) {
            dispatch(SupplierActions.getSupplier())
        }

        dispatch(AnalyticsActions.postAnalytics('[Settings] componentDidMount', null))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (managers.length > 0) {
            const manager = managers.find(item => item.email === attributes.email)
            if (manager) setIsManager(true)
        }
    }, [attributes, managers])

    useEffect(() => {
        if (Object.keys(updatedPermissions).length > 0) {
            setTimeout(() => {
                setUpdatedPermissions({})
            }, 100)
        }
    }, [updatedPermissions])

    const onSubmit = values => {
        const existed = managers.find(({ email }) => email === values.email)
        if (existed) {
            setModalIsOpen(false)
            Alert('A manager already exists with this email.', 'warning', 10000)
            return
        }

        const supplier = JSON.parse(localStorage.getItem('supplier'))
        const body = {
            ...values,
            supplierId: supplier.supplierId,
            companyName: supplier.companyName,
            mainSupplierEmail: supplier.primaryContactEmail,
        }

        dispatch(
            ManagersActions.addManager(body, (message, type) => {
                setModalIsOpen(false)
                Alert(message, type, 10000)
            })
        )
    }

    const showSuccess = async (body, email, key) => {
        const updatedPermissionKey = Object.keys(body)?.[0]
        const id = `${updatedPermissionKey}_${email}`
        if (!updatedPermissions[id]) {
            setUpdatedPermissions({ ...updatedPermissions, [id]: { email, permissions: key, body } })
            setTimeout(() => {
                removeUpdatedPermission(id)
            }, 3000)
        }
    }

    const removeUpdatedPermission = id => {
        setUpdatedPermissions(_.omit(updatedPermissions, [`${id}`]))
    }

    const updateRole = (body, email, key) => {
        dispatch(ManagersActions.updateManagerRole(body, email, key, showSuccess))
    }

    if (loadingManagers) {
        return (
            <div className="centerLoader">
                <img src={AnimatedLogo} width="128px" alt="Full screen loading spinner" />
            </div>
        )
    }
    return (
        <div>
            <h1>Settings</h1>
            {supplier && <Profile {...{ openModal: () => setModalPasswordIsOpen(true) }} />}
            <ChangePassword modalIsOpen={modalPasswordIsOpen} closeModal={() => setModalPasswordIsOpen(false)} />
            <Table
                title="Managers"
                subtitle={`Note: Managers can only be invited, disabled or modified by the main account (${supplier?.primaryContactEmail})`}
                data={managers}
                columns={columnsData({
                    updateManager: body => dispatch(ManagersActions.updateManager(body, (message, type) => Alert(message, type))),
                    loadingUpdateManager,
                    loadingUpdateRole,
                    updateRole,
                    isManager,
                    updatedPermissions,
                })}
                emptyStateText={getManagersError?.message ? `Error: ${getManagersError?.message}` : 'No managers were found'}
                renderCustomButton={
                    isManager
                        ? false
                        : () => (
                              <Button className="settingsButton" onClick={() => setModalIsOpen(true)}>
                                  Invite
                              </Button>
                          )
                }
            />

            <Modal isOpen={modalIsOpen} onRequestClose={() => setModalIsOpen(false)} className="modal-container" overlayClassName="modal-overlay">
                <div className="module">
                    <div className="module-header">
                        <h2>Invite manager</h2>
                        <div onClick={() => setModalIsOpen(false)}>
                            <IconContext.Provider value={{ className: 'modal-close-button' }}>
                                <AiFillCloseCircle />
                            </IconContext.Provider>
                        </div>
                    </div>
                    <Formik
                        enableReinitialize={true}
                        initialValues={{
                            email: '',
                            customerSupportTickets: false,
                            pendingOrders: false,
                            invoices: false,
                            directoryPage: false,
                            transactionsPage: false,
                            invoicesPage: false,
                            settingsPage: false,
                        }}
                        validationSchema={emailSchema}
                        onSubmit={onSubmit}
                    >
                        <Form className="inviteManagerForm">
                            <Field name="email">
                                {({ field, form, meta }) => (
                                    <Input type="email" touched={meta.touched} error={meta.error} {...field} labelAbove="MANAGER EMAIL" onBlurCapture={e => form.setFieldValue(e.target.name, e.target.value.trim().toLowerCase())} />
                                )}
                            </Field>
                            <div className="input-container">
                                <span className="input-label">EMAIL NOTIFICATIONS</span>
                                <div className="modal-managers-permissions">
                                    {notifications.map((notification, index) => (
                                        <div key={index} className="admins-checkbox">
                                            <label>
                                                <Field type="checkbox" name={notification.value} />
                                                {notification.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="input-container">
                                <span className="input-label">PERMISSIONS TO VIEW</span>
                                <div className="modal-managers-permissions">
                                    {Object.keys(SUPPLIERS_PERMISSIONS_TO_VIEW).map(permitionKey => {
                                        const permition = SUPPLIERS_PERMISSIONS_TO_VIEW[permitionKey]
                                        return (
                                            <div key={permition.value} className="admins-checkbox">
                                                <label>
                                                    <Field type="checkbox" name={permition.value} />
                                                    {permition.label}
                                                </label>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <Button loading={loadingAddManager} type="submit" className="modal-submit-button">
                                SEND
                            </Button>
                        </Form>
                    </Formik>
                </div>
            </Modal>
        </div>
    )
}

export default Settings
